import { eventListeners, Placement } from '@popperjs/core';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { usePopper } from 'react-popper';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Text } from '@dynatrace/strato-components/typography';

import {
  tooltipAnimationWrapperCSS,
  tooltipElementCSS,
  tooltipOffsetVar,
} from './tooltip.sty.js';
import { TooltipProps } from './Tooltip.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import {
  observeAnimationDone,
  observeOverlaySize,
} from '../utils/popper-modifiers.js';

const TOOLTIP_OFFSET = 4;

interface TooltipElementProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps,
    Pick<TooltipProps, 'placement' | 'fallbackPlacements' | 'id'> {
  /** Reference to the trigger element */
  triggerElement?: Element;
  /** Callback triggered when the mouse leaves the tooltip element. */
  onMouseLeave?: (e: ReactMouseEvent<HTMLDivElement>) => void;
  /** Callback triggered when the tooltip hover state changes. */
  onTooltipHovered?: (isTooltipHovered: boolean) => void;
}

// #endregion

/** The tooltip element / portal that is being rendered into the tooltip */
export const TooltipElement = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  TooltipElementProps
>((props: TooltipElementProps, forwardedRef) => {
  const {
    children,
    placement,
    triggerElement,
    id,
    fallbackPlacements: fallbackPlacementsProp,
    onMouseLeave,
    onTooltipHovered,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);

  // Forwards the ref to the opened tooltip.
  // DISCUSS: This stays on the tooltip element rather than the wrapping span
  // in order to not break the behavior and api. We should definitely
  // think about switching this up.
  const mergedTooltipRef = useMergeRefs([forwardedRef, setTooltipRef]);

  // Defines fallback placements. In order to flip _correctly_, we adjust
  // the fallback list based on the desired positioning.
  const fallbackPlacements = useMemo<Placement[] | undefined>(() => {
    if (fallbackPlacementsProp) {
      return fallbackPlacementsProp;
    }

    switch (placement) {
      case 'top':
        return ['bottom', 'left', 'right'];
      case 'bottom':
        return ['top', 'left', 'right'];
      case 'left':
        return ['right', 'top', 'bottom'];
      case 'right':
        return ['left', 'top', 'bottom'];
    }
  }, [placement, fallbackPlacementsProp]);

  const { styles, attributes } = usePopper(triggerElement, tooltipRef, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, TOOLTIP_OFFSET],
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements,
        },
      },
      // TODO: Deprecation is only happening to the public export, the internal function will still be available
      // can be removed with APPDEV-12712
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      observeOverlaySize,
      observeAnimationDone,
      eventListeners,
    ],
  });

  // Get the zIndex for the stacked overlay to apply to the tooltip
  const stackingContext = useOverlayStacking(undefined, 'overlay');

  const tooltipHovered = useRef(false);

  const handleMouseLeaveTooltip = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      tooltipHovered.current = false;
      onMouseLeave?.(e);
      onTooltipHovered?.(tooltipHovered.current);
    },
    [onMouseLeave, onTooltipHovered],
  );

  return (
    <div
      ref={mergedTooltipRef}
      style={{
        ...styles.popper,
        zIndex: stackingContext.style.zIndex,
        ...consumerStyle,
        ...assignInlineVars({ [tooltipOffsetVar]: `${TOOLTIP_OFFSET}px` }),
      }}
      className={clsx(tooltipElementCSS, consumerClassName)}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      role="tooltip"
      onMouseEnter={() => {
        tooltipHovered.current = true;
        onTooltipHovered?.(tooltipHovered.current);
      }}
      onMouseLeave={handleMouseLeaveTooltip}
      {...attributes.popper}
      id={id}
    >
      <div className={tooltipAnimationWrapperCSS}>
        {typeof children === 'string' ? (
          <Text textStyle="small" as="p">
            {children}
          </Text>
        ) : (
          children
        )}
      </div>
    </div>
  );
});

(
  TooltipElement as typeof TooltipElement & { displayName: string }
).displayName = 'TooltipElement';
